import toast from 'react-hot-toast';
import {useModal} from '@hooks';
import {useMemo} from 'react';

import {
  useAppDispatch,
  useAppSelector,
  Meeting,
  MeetingRoom,
  PartOfDay,
  getCanUserAddRoomToExistingMeeting,
  getDefaultBuildingId,
  getMeetingRoomsByMeetingId,
  getMeetingsByDate,
  getMeetingsLoadingStatusByDate,
  updateMeeting,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {Button, SwiperHorizontal} from '@molecules';
import {BigMapModal, CreateEventManualRoomSelectionCard, LoaderTile, MeetingDetailsCard, Tile} from '@organisms';
import {Icon} from '@atoms';
import {addDays} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {MeetingRoomInfoFooter} from './MeetingRoomInfoFooter';
import {MeetingRoomMapView} from './MeetingRoomMapView';
import {
  describeMeeting,
  getFormatedMeetingTime,
  selectMeetingsToShow,
  defaultSelectionOptions,
} from '@lib/store/src/util';
import {trackEvent} from '@lib/infrastructure';
import {BREAKPOINT_SIZES} from '@constants';

const UpcomingMeetingTile = ({meeting, time}: {meeting: Meeting; time: Date}) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();

  // for handling clicks
  const {openModal, closeModal, setModalPages, setBigPages} = useModal();

  const meetingRooms = useAppSelector((state) => getMeetingRoomsByMeetingId(state, meeting.id));
  const location = meetingRooms.at(0);

  const startTime = new Date(meeting.startDateTime);
  const endTime = new Date(meeting.endDateTime);
  const {label, isOngoing, countdown} = describeMeeting(time, startTime, endTime, t);

  const timeLabel = getFormatedMeetingTime(meeting, t);

  const defaultBuildingId = useAppSelector(getDefaultBuildingId);

  const canUserAddRoom = useAppSelector((state) => getCanUserAddRoomToExistingMeeting(state, meeting.id)) && !location;

  const handleInfoClick = () => {
    const modalCard = (
      <MeetingDetailsCard
        date={meeting.startDateTime}
        meeting={meeting}
      />
    );
    trackEvent(`Home_UpcomingMeetingsTile__ViewDetails`);
    setModalPages([modalCard]);
    openModal();
  };

  const handleAddRoomClick = () => {
    setModalPages([
      <CreateEventManualRoomSelectionCard
        defaultBuildingId={defaultBuildingId}
        origin="CalendarOverview_AdHocRoom"
        endDateTime={meeting.endDateTime}
        onClose={closeModal}
        onSelect={async (value: MeetingRoom) => {
          const {success} = await dispatch(
            withAsyncThunkErrorHandling(() =>
              updateMeeting({
                origin: 'AddRoomToExistingMeeting',
                meetingId: meeting.id,
                systemTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                rooms: [...meetingRooms.map((room) => room.email), value.email],
              }),
            ),
          );
          if (success) {
            closeModal();
            toast.success(t('meeting:AddMeetingRoomToMeetingSuccessMessage', {roomName: value.displayName}));
          }
        }}
        selectedRooms={meetingRooms.map((room) => room.email)}
        startDateTime={meeting.startDateTime}
      />,
    ]);

    trackEvent('Home_UpcomingMeetingsTile__AddRoomClicked');
    openModal();
  };

  const expandMap = () => {
    if (!location) return;
    const mapView = (
      <BigMapModal
        title="Meeting room"
        main={
          <MeetingRoomMapView
            room={location}
            interactive={true}
          />
        }
        footer={<MeetingRoomInfoFooter room={location} />}
      />
    );

    trackEvent('Home_UpcomingMeetingsTile__ExpandMap', {
      buildingName: location.buildingName,
      floorName: location.floorName,
      roomName: location.displayName,
    });

    setModalPages([mapView]);
    setBigPages([mapView]);
    openModal();
  };

  return (
    <div
      className="bg-beige-500* flex flex-col  rounded-lg p-4 w-full overflow-hidden cursor-pointer"
      onClick={handleInfoClick}>
      <div className="grid grid-cols-[auto_auto_1fr] items-center gap-2 mb-4 w-full">
        <Icon
          icon="calendar"
          size={'22px'}
          className="-translate-y-px"
        />
        <strong className="">{timeLabel}</strong>
        <small className="text-right">{isOngoing ? label : countdown}</small>
      </div>

      <div className="flex flex-col mr-auto min-w-0 gap-6 w-full">
        <p className="line-clamp-2">{meeting.title}</p>

        <div className="mr-auto">
          {location && (
            <Button
              className="grid grid-cols-[auto_1fr] bg-connecting-green-500* text-white"
              aria-label={t('GetInformationAboutYourUpcomingMeeting')}
              onClick={(e) => {
                e.stopPropagation();
                expandMap();
              }}
              size="small"
              button={'tertiary'}>
              <Icon
                icon="map"
                color="white"
                className="size-4 flex-shrink-0"
              />
              <div className="truncate">{location.displayName}</div>
            </Button>
          )}

          {!location && canUserAddRoom && (
            <Button
              className="grid grid-cols-[auto_1fr]"
              aria-label={t('GetInformationAboutYourUpcomingMeeting')}
              onClick={(e) => {
                e.stopPropagation();
                handleAddRoomClick();
              }}
              size="small"
              button={'primary light'}>
              <Icon
                icon="plus"
                color="black"
                className="size-4 flex-shrink-0"
              />
              <span className="truncate">{t('translation:AddRoom')}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const UpcomingMeetingsTile = ({date, time, partOfDay}: {date: Date; time: Date; partOfDay: PartOfDay}) => {
  const {t} = useTranslation();

  const tomorrow = useMemo(() => addDays(date, 1), [date]);

  const meetings = useAppSelector((state) => getMeetingsByDate(state, date));
  const tomorrowMeetings = useAppSelector((state) => getMeetingsByDate(state, tomorrow));
  const isMeetingsLoaded = useAppSelector((state) => getMeetingsLoadingStatusByDate(state, date)) === 'Loaded';

  const visibleMeetings = useMemo(() => {
    return selectMeetingsToShow([...meetings, ...tomorrowMeetings], time, {
      ...defaultSelectionOptions,
      excludeMeetingsOnDifferentDays: partOfDay === 'WorkingHours',
    });
  }, [meetings, tomorrowMeetings, time, partOfDay]);

  const slidesPerView = visibleMeetings.length > 1 ? 1.1 : 1;
  const slidesPerViewMd = visibleMeetings.length === 1 ? 1 : visibleMeetings.length === 2 ? 2 : 2.1;

  if (!isMeetingsLoaded) {
    return <LoaderTile rows={[[115]]} />;
  }

  if (!visibleMeetings.length) {
    return null;
  }

  return (
    <Tile title={t('meeting:UpcomingMeeting')}>
      <SwiperHorizontal
        className="w-full [&>.swiper-wrapper]:p-0 rounded-lg"
        slidesPerView={slidesPerView}
        breakpoints={{
          [BREAKPOINT_SIZES.medium]: {slidesPerView: slidesPerViewMd},
        }}
        spaceBetween={16}>
        {visibleMeetings.map((m) => (
          <UpcomingMeetingTile
            key={`TILE_${m.id}`}
            time={time}
            meeting={m}
          />
        ))}
      </SwiperHorizontal>
    </Tile>
  );
};
